import React, { useState, useContext, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  TextField,
  DialogContent,
  Snackbar,
  Alert,
  IconButton,
  Divider,
  Slide
} from '@mui/material'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { formatDate } from '../../../Utils/Format'
import { apiUrl } from '../../../config/api'
import { DocumentViewer } from '../../Utils/DocumentViewer'
import { misDocumentosContext } from '../../../contexts/MisDocumentosContext'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const brandColor = () => (window.$brand === 'PRB' ? '#17afb6' : '#102a46')
const brandRadius = () => (window.$brand === 'PRB' ? 0 : 2)
const brandHlBg = () => (window.$brand === 'PRB' ? '#41bcfc14' : '#4593db14')
const lang = (en, es) => (window.$lang === 'en' ? en : es || en)

const countSubcategoryDocs = (s) => {
  let n = s.docs ? s.docs.length : 0
  if (s.files)
    s.files.forEach((f) => {
      n += f.docs ? f.docs.length : 0
    })
  return n
}
const countWorkspaceDocs = (w) =>
  (w.subcategories || []).reduce((a, s) => a + countSubcategoryDocs(s), 0)
const countFileDocs = (f) => (f.docs ? f.docs.length : 0)

// ---------------------------------------------------------------------------
// URL <-> stack sync helpers
// ---------------------------------------------------------------------------
const stackToParams = (stack) => {
  const params = new URLSearchParams()
  for (const entry of stack) {
    if (entry.type === 'client') {
      params.set('view', 'client')
    }
    if (entry.type === 'company') {
      params.set('company', entry.company.id)
    }
    if (entry.type === 'workspace') {
      params.set('workspace', entry.workspace.name_en)
    }
    if (entry.type === 'subcategory') {
      params.set('subcategory', entry.subcategory.subcategory_en)
    }
    if (entry.type === 'file') {
      params.set('file', entry.file.id)
    }
  }
  return params
}

// Derives the navigation stack from URL params + live client data.
// Called on every render so it always reflects the latest data — no timing issues.
const rehydrateStack = (params, client) => {
  const stack = []
  const view = params.get('view')
  const companyId = params.get('company')
  const workspaceName = params.get('workspace')
  const subcatName = params.get('subcategory')
  const fileId = params.get('file')

  // Enter the block if any navigable param is present
  if (view === 'client' || workspaceName || companyId) {
    if (companyId) {
      // ── Company branch ──────────────────────────────────────────────────
      const company = client.companies?.find((c) => String(c.id) === companyId)
      if (!company) return stack
      stack.push({ type: 'company', label: company.name, company })

      if (workspaceName) {
        const ws = company.workspaces?.find((w) => w.name_en === workspaceName)
        if (!ws) return stack
        stack.push({
          type: 'workspace',
          label: lang(ws.name_en, ws.name_es),
          workspace: ws,
          company
        })

        if (subcatName) {
          const sub = ws.subcategories?.find(
            (s) => s.subcategory_en === subcatName
          )
          if (!sub) return stack
          stack.push({
            type: 'subcategory',
            label: lang(sub.subcategory_en, sub.subcategory_es),
            subcategory: sub,
            workspace: ws,
            company
          })

          if (fileId) {
            const file = sub.files?.find((f) => String(f.id) === fileId)
            if (!file) return stack
            stack.push({
              type: 'file',
              label: file.name,
              file,
              subcategory: sub,
              workspace: ws,
              company
            })
          }
        }
      }
    } else {
      // ── Client branch ───────────────────────────────────────────────────
      stack.push({ type: 'client', label: client.name })

      if (workspaceName) {
        const ws = client.workspaces?.find((w) => w.name_en === workspaceName)
        if (!ws) return stack
        stack.push({
          type: 'workspace',
          label: lang(ws.name_en, ws.name_es),
          workspace: ws,
          company: null
        })

        if (subcatName) {
          const sub = ws.subcategories?.find(
            (s) => s.subcategory_en === subcatName
          )
          if (!sub) return stack
          stack.push({
            type: 'subcategory',
            label: lang(sub.subcategory_en, sub.subcategory_es),
            subcategory: sub,
            workspace: ws,
            company: null
          })

          if (fileId) {
            const file = sub.files?.find((f) => String(f.id) === fileId)
            if (!file) return stack
            stack.push({
              type: 'file',
              label: file.name,
              file,
              subcategory: sub,
              workspace: ws,
              company: null
            })
          }
        }
      }
    }
  }
  return stack
}

// ---------------------------------------------------------------------------
// DirectoryRow — a single row item in the current level list
// ---------------------------------------------------------------------------
const DirectoryRow = ({
  icon,
  label,
  meta,
  metaHighlight,
  onClick,
  actions,
  selected
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 2,
      py: 1.25,
      borderBottom: '1px solid',
      borderColor: 'divider',
      cursor: onClick ? 'pointer' : 'default',
      borderLeft: selected
        ? `3px solid ${brandColor()}`
        : '3px solid transparent',
      bgcolor: selected ? brandHlBg() : 'transparent',
      '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
      transition: 'background 0.1s'
    }}
  >
    <Box
      sx={{ color: 'text.secondary', display: 'flex', mr: 1.5, flexShrink: 0 }}
    >
      {icon}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }} onClick={onClick}>
      <Typography variant='body2' noWrap fontWeight={500}>
        {label}
      </Typography>
      {meta && (
        <Typography
          variant='caption'
          noWrap
          sx={{ color: metaHighlight ? brandColor() : 'text.secondary' }}
        >
          {meta}
        </Typography>
      )}
    </Box>
    {actions && (
      <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
        {actions}
      </Box>
    )}
    {onClick && (
      <ChevronRightIcon
        sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5, flexShrink: 0 }}
      />
    )}
  </Box>
)

// ---------------------------------------------------------------------------
// DocumentPanel — slides in from the right when a document is clicked
// ---------------------------------------------------------------------------
const DocumentPanel = ({ document, context, onClose, userId }) => {
  const { t } = useTranslation()

  const [snackOpen, setSnackOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewUri, setPreviewUri] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const openPreview = () => {
    setPreviewLoading(true)
    setPreviewOpen(true)
    fetch(apiUrl('GetDocumentSASToken'), {
      method: 'POST',
      body: JSON.stringify({ documentUrl: document.url })
    })
      .then((res) => res.json())
      .then((data) => {
        setPreviewUri(data.uri)
        setPreviewLoading(false)
      })
      .catch(() => setPreviewLoading(false))
  }

  const downloadDoc = () => {
    const clientOrCompanyId = context.company
      ? context.company.id
      : context.client.id
    setSnackOpen(true)
    const filename = /[^/]*$/.exec(document.url)[0]
    const rqData = {
      container:
        document.source === 'docusign'
          ? 'prbdocusign'
          : 'client-' + clientOrCompanyId,
      filename,
      documentUrl: document.url,
      brandLabel: window.$brand,
      loggedUserId: userId
    }
    ;(async () => {
      const res = await fetch(apiUrl('DownloadFileFromStorage'), {
        method: 'POST',
        body: JSON.stringify(rqData)
      })
      const reader = res.body.getReader()
      let chunks = new Uint8Array(0)
      const extToMime = {
        '.pdf': 'application/pdf',
        '.doc': 'application/msword',
        '.docx':
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.xls': 'application/vnd.ms-excel',
        '.xlsx':
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.ppt': 'application/vnd.ms-powerpoint',
        '.pptx':
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        '.rtf': 'application/rtf',
        '.eml': 'message/rfc822',
        '.msg': 'application/vnd.ms-outlook',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.png': 'image/png',
        '.gif': 'image/gif',
        '.bmp': 'image/bmp',
        '.txt': 'text/plain',
        '.csv': 'text/csv',
        '.xml': 'application/xml',
        '.zip': 'application/zip'
      }
      const process = ({ done, value }) => {
        if (done) {
          const mime = extToMime[document.extension?.toLowerCase()] || ''
          const blob = new Blob([chunks], { type: mime })
          const a = window.document.createElement('a')
          window.document.body.appendChild(a)
          a.style = 'display:none'
          a.href = URL.createObjectURL(blob)
          a.download = document.filename
          a.click()
          URL.revokeObjectURL(a.href)
          return
        }
        const tmp = new Uint8Array(chunks.length + value.length)
        tmp.set(chunks)
        tmp.set(value, chunks.length)
        chunks = tmp
        return reader.read().then(process)
      }
      reader.read().then(process)
    })()
  }

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 320 },
        flexShrink: 0,
        borderLeft: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper'
      }}
    >
      {/* Panel header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          bgcolor: brandHlBg()
        }}
      >
        <DescriptionOutlinedIcon
          sx={{ fontSize: 18, color: brandColor(), mr: 1 }}
        />
        <Typography
          variant='subtitle2'
          fontWeight={700}
          sx={{ flexGrow: 1 }}
          noWrap
        >
          {document.filename}
        </Typography>
        <IconButton size='small' onClick={onClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      {/* Panel body */}
      <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          {document.visible ? (
            <Chip
              icon={<VisibilityIcon />}
              label={t('Visible')}
              color='success'
              size='small'
            />
          ) : (
            <Chip
              icon={<VisibilityOffIcon />}
              label={t('Hidden')}
              size='small'
            />
          )}
          {document.expiration && (
            <Chip
              label={`${t('Expires')}: ${formatDate(t, document.expiration)}`}
              size='small'
              color='warning'
            />
          )}
        </Box>

        {document.notes && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            {document.notes}
          </Typography>
        )}

        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          sx={{ mb: 2.5 }}
        >
          {t(document.source !== 'dwcopy' ? 'Uploaded' : 'Updated')}:{' '}
          {formatDate(t, document.modified)}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant='outlined'
            fullWidth
            sx={{ borderRadius: brandRadius() }}
            onClick={openPreview}
          >
            {t('Preview')}
          </Button>
          <Button
            variant='contained'
            fullWidth
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{ bgcolor: brandColor(), borderRadius: brandRadius() }}
            onClick={downloadDoc}
          >
            {t('Download')}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackOpen}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity='info'
          icon={<HourglassTopIcon />}
          elevation={6}
          variant='filled'
        >
          {t('downloading_document')}
        </Alert>
      </Snackbar>

      {/* Preview modal */}
      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false)
          setPreviewUri(null)
        }}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: { height: '90vh', display: 'flex', flexDirection: 'column' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
          <DescriptionOutlinedIcon sx={{ mr: 1, color: brandColor() }} />
          <Typography
            variant='subtitle1'
            fontWeight={600}
            sx={{ flexGrow: 1 }}
            noWrap
          >
            {document.filename}
          </Typography>
          <IconButton
            size='small'
            onClick={() => {
              setPreviewOpen(false)
              setPreviewUri(null)
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ flexGrow: 1, p: 0, overflow: 'hidden' }}>
          {previewLoading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}
            >
              <Typography color='text.secondary'>
                {t('Loading preview...')}
              </Typography>
            </Box>
          )}
          {!previewLoading && previewUri && (
            <DocumentViewer
              fileUrl={previewUri}
              extension={document.extension}
              height='100%'
            />
          )}
          {!previewLoading && !previewUri && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}
            >
              <Typography color='text.secondary'>
                {t('Preview not available.')}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// MisDocumentosDirectory — main exported component
// ---------------------------------------------------------------------------
export const MisDocumentosDirectory = ({
  userId,
  subcategories: subcategoriesProp
}) => {
  const { t } = useTranslation()
  const { data } = useContext(misDocumentosContext)
  const history = useHistory()
  const location = useLocation()
  const [filterText, setFilterText] = useState('')

  const matchesFilter = (text) =>
    !filterText.trim() ||
    (text || '').toLowerCase().includes(filterText.toLowerCase())

  // Derive stack directly from URL + live data on every render.
  // This avoids all timing issues — when data finishes loading React re-renders
  // and rehydrateStack resolves correctly against the now-complete data tree.
  const client =
    data && data.id && data.id !== -1 ? { ...data, type: 'client' } : null
  const params = new URLSearchParams(location.search)
  const stack =
    client && params.toString() ? rehydrateStack(params, client) : []
  const currentEntry = stack[stack.length - 1] ?? null

  const docId = params.get('doc')

  // On initial load, if a doc is specified without the necessary workspace/subcategory params, find that doc in the tree
  // and rewrite the URL with the correct params. This supports deep linking directly to a doc without requiring the full
  // path in the URL. Used in the calendar view to navigate using doc id.
  useEffect(() => {
    if (!client) return

    const rawDocId = params.get('doc')
    if (
      !rawDocId ||
      params.get('workspace') ||
      params.get('company') ||
      params.get('view')
    )
      return
    if (!client.workspaces?.length && !client.companies?.length) return

    // Walk the entire tree to find the doc
    const findDoc = () => {
      // Helper to search within a workspace tree (client-level or company-level)
      const searchWorkspaces = (workspaces, company = null) => {
        for (const ws of workspaces || []) {
          for (const sub of ws.subcategories || []) {
            // Doc directly on subcategory
            if ((sub.docs || []).some((d) => String(d.id) === rawDocId)) {
              return { ws, sub, file: null, company }
            }
            // Doc inside a file
            for (const file of sub.files || []) {
              if ((file.docs || []).some((d) => String(d.id) === rawDocId)) {
                return { ws, sub, file, company }
              }
            }
          }
        }
        return null
      }

      // Search client-level workspaces
      const clientResult = searchWorkspaces(client.workspaces, null)
      if (clientResult) return clientResult

      // Search each company's workspaces
      for (const company of client.companies || []) {
        const companyResult = searchWorkspaces(company.workspaces, company)
        if (companyResult) return companyResult
      }

      return null
    }

    const found = findDoc()
    if (!found) return

    const { ws, sub, file, company } = found
    const p = new URLSearchParams()

    if (company) {
      p.set('company', company.id)
    } else {
      p.set('view', 'client')
    }
    p.set('workspace', ws.name_en)
    p.set('subcategory', sub.subcategory_en)
    if (file) p.set('file', file.id)
    p.set('doc', rawDocId)

    history.replace({ search: '?' + p.toString() })
  }, [client]) // re-run only if the client changes, not on every render

  // Helper: find a doc by id across the current level's docs
  const findDocInEntry = (entry) => {
    if (!entry || !docId) return null
    if (entry.type === 'subcategory') {
      return (
        (entry.subcategory.docs || []).find((d) => String(d.id) === docId) ??
        entry.subcategory.files
          ?.flatMap((f) => f.docs || [])
          .find((d) => String(d.id) === docId) ??
        null
      )
    }
    if (entry.type === 'file') {
      return (entry.file.docs || []).find((d) => String(d.id) === docId) ?? null
    }
    return null
  }

  const openDoc = findDocInEntry(currentEntry)
  const openDocContext = openDoc
    ? {
        client,
        company: currentEntry?.company ?? null,
        workspace: currentEntry?.workspace ?? null,
        subcategory: currentEntry?.subcategory ?? null,
        file: currentEntry?.file ?? null
      }
    : null

  if (!client) return null

  const closeDoc = () => {
    const p = new URLSearchParams(location.search)
    p.delete('doc')
    history.push({ search: p.toString() ? '?' + p.toString() : '' })
  }

  // Navigation — writes to URL; stack is re-derived on next render
  const navigateTo = (entry) => {
    setFilterText('')
    const newStack = [...stack, entry]
    const p = stackToParams(newStack)
    p.delete('doc')
    history.push({ search: p.toString() ? '?' + p.toString() : '' })
  }

  const navigateToBreadcrumb = (index) => {
    setFilterText('')
    const newStack = stack.slice(0, index + 1)
    const p = stackToParams(newStack)
    p.delete('doc')
    history.push({ search: p.toString() ? '?' + p.toString() : '' })
  }

  const navigateToRoot = () => {
    setFilterText('')
    history.push({ search: '' })
  }

  // Build breadcrumb items
  const rootLabel = window.$brand === 'ZAP' ? t('Condominium') : t('Directory')
  const breadcrumbItems = [
    { label: rootLabel, index: -1 },
    ...stack.map((entry, i) => ({ label: entry.label, index: i }))
  ]

  // Determine what to render at the current level
  const renderCurrentLevel = () => {
    // ROOT: show client row + companies
    if (stack.length === 0) {
      const visibleCompanies = (client.companies || []).filter((c) =>
        (c.workspaces || []).some((ws) => countWorkspaceDocs(ws) > 0)
      )
      const hasCompanies = visibleCompanies.length > 0

      const clientHasDocs = (client.workspaces || []).some(
        (ws) => countWorkspaceDocs(ws) > 0
      )

      const clientLabel =
        window.$brand === 'ZAP' ? t('Condominium') : t('Client')
      const companiesLabel =
        window.$brand === 'ZAP'
          ? t('Properties')
          : window.$brand === 'IDB'
            ? t('Financings')
            : t('Companies')

      return (
        <>
          {clientHasDocs && (
            <>
              <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={600}
                >
                  {clientLabel}
                </Typography>
              </Box>
              <DirectoryRow
                icon={<FolderOutlinedIcon fontSize='small' />}
                label={client.name}
                meta={client.license ? `ID: ${client.license}` : undefined}
                onClick={() =>
                  navigateTo({ type: 'client', label: client.name })
                }
              />
            </>
          )}
          {hasCompanies && (
            <>
              <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={600}
                >
                  {companiesLabel}
                </Typography>
              </Box>
              {visibleCompanies
                .filter((company) => matchesFilter(company.name))
                .map((company) => (
                  <DirectoryRow
                    key={company.id}
                    icon={<FolderOutlinedIcon fontSize='small' />}
                    label={company.name}
                    meta={
                      company.license ? `ID: ${company.license}` : undefined
                    }
                    onClick={() =>
                      navigateTo({
                        type: 'company',
                        label: company.name,
                        company
                      })
                    }
                  />
                ))}
            </>
          )}
        </>
      )
    }

    const entry = currentEntry

    // CLIENT: show its workspaces
    if (entry.type === 'client') {
      return (
        <>
          {(client.workspaces || [])
            .filter((ws) => matchesFilter(lang(ws.name_en, ws.name_es)))
            .filter((ws) => countWorkspaceDocs(ws) > 0)
            .map((ws) => (
              <DirectoryRow
                key={ws.name_en}
                icon={<FolderOutlinedIcon fontSize='small' />}
                label={lang(ws.name_en, ws.name_es)}
                meta={`${countWorkspaceDocs(ws)} ${t('Documents')}`}
                metaHighlight={countWorkspaceDocs(ws) > 0}
                onClick={() =>
                  navigateTo({
                    type: 'workspace',
                    label: lang(ws.name_en, ws.name_es),
                    workspace: ws,
                    company: null
                  })
                }
              />
            ))}
        </>
      )
    }

    // COMPANY: show its workspaces
    if (entry.type === 'company') {
      const company = entry.company
      return (
        <>
          {(company.workspaces || [])
            .filter((ws) => matchesFilter(lang(ws.name_en, ws.name_es)))
            .filter((ws) => countWorkspaceDocs(ws) > 0)
            .map((ws) => (
              <DirectoryRow
                key={ws.name_en}
                icon={<FolderOutlinedIcon fontSize='small' />}
                label={lang(ws.name_en, ws.name_es)}
                meta={`${countWorkspaceDocs(ws)} ${t('Documents')}`}
                metaHighlight={countWorkspaceDocs(ws) > 0}
                onClick={() =>
                  navigateTo({
                    type: 'workspace',
                    label: lang(ws.name_en, ws.name_es),
                    workspace: ws,
                    company
                  })
                }
              />
            ))}
        </>
      )
    }

    // WORKSPACE: show subcategories
    if (entry.type === 'workspace') {
      const { workspace, company } = entry
      return (
        <>
          {(workspace.subcategories || [])
            .filter((sub) =>
              matchesFilter(lang(sub.subcategory_en, sub.subcategory_es))
            )
            .filter((sub) => countSubcategoryDocs(sub) > 0)
            .map((sub) => (
              <DirectoryRow
                key={sub.subcategory_en}
                icon={<FolderOutlinedIcon fontSize='small' />}
                label={lang(sub.subcategory_en, sub.subcategory_es)}
                meta={`${countSubcategoryDocs(sub)} ${t('Documents')}`}
                metaHighlight={countSubcategoryDocs(sub) > 0}
                onClick={() =>
                  navigateTo({
                    type: 'subcategory',
                    label: lang(sub.subcategory_en, sub.subcategory_es),
                    subcategory: sub,
                    workspace,
                    company
                  })
                }
              />
            ))}
        </>
      )
    }

    // SUBCATEGORY: show files + direct docs
    if (entry.type === 'subcategory') {
      const { subcategory, workspace, company } = entry
      const files = subcategory.files || []
      const docs = subcategory.docs || []
      return (
        <>
          {files
            .filter((file) => matchesFilter(file.name))
            .filter((file) => countFileDocs(file) > 0)
            .map((file) => (
              <DirectoryRow
                key={file.id}
                icon={<FolderOutlinedIcon fontSize='small' />}
                label={file.name}
                meta={`${file.docs ? file.docs.length : 0} ${t('Documents')}`}
                metaHighlight={file.docs && file.docs.length > 0}
                onClick={() =>
                  navigateTo({
                    type: 'file',
                    label: file.name,
                    file,
                    subcategory,
                    workspace,
                    company
                  })
                }
              />
            ))}
          {docs
            .filter((doc) => matchesFilter(doc.filename))
            .map((doc) => (
              <DirectoryRow
                key={doc.id}
                icon={<DescriptionOutlinedIcon fontSize='small' />}
                label={doc.filename}
                meta={formatDate(t, doc.modified)}
                selected={String(doc.id) === docId}
                onClick={() => {
                  const p = new URLSearchParams(location.search)
                  p.set('doc', doc.id)
                  history.push({ search: '?' + p.toString() })
                }}
              />
            ))}
        </>
      )
    }

    // FILE: show its docs
    if (entry.type === 'file') {
      const { file } = entry
      const docs = file.docs || []
      return (
        <>
          {docs
            .filter((doc) => matchesFilter(doc.filename))
            .map((doc) => (
              <DirectoryRow
                key={doc.id}
                icon={<DescriptionOutlinedIcon fontSize='small' />}
                label={doc.filename}
                meta={formatDate(t, doc.modified)}
                selected={String(doc.id) === docId}
                onClick={() => {
                  const p = new URLSearchParams(location.search)
                  p.set('doc', doc.id)
                  history.push({ search: '?' + p.toString() })
                }}
              />
            ))}
          {docs.length === 0 && (
            <Box sx={{ p: 3, color: 'text.secondary' }}>
              <Typography variant='body2'>
                {t('No documents here yet.')}
              </Typography>
            </Box>
          )}
        </>
      )
    }

    return null
  }

  return (
    <>
      {/* ---- Filter ---- */}
      <TextField
        placeholder={t('Filter')}
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        size='small'
        variant='standard'
        sx={{ minWidth: 160, flexShrink: 0, marginBottom: 1, width: '100%' }}
      />
      <Paper
        elevation={0}
        variant='outlined'
        sx={{
          borderRadius: window.$brand === 'BLP' ? 3 : 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 480
        }}
      >
        {/* ---- Top bar: breadcrumbs + action buttons ---- */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1,
            bgcolor: 'background.paper'
          }}
        >
          <Breadcrumbs
            separator='›'
            sx={{
              flexGrow: 1,
              minWidth: 0,
              overflow: 'hidden',
              '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
              '& .MuiBreadcrumbs-li': { minWidth: 0 }
            }}
          >
            {breadcrumbItems.map((item, i) => {
              const isLast = i === breadcrumbItems.length - 1
              return isLast ? (
                <Typography
                  key={i}
                  variant='body2'
                  fontWeight={600}
                  color='text.primary'
                  noWrap
                >
                  {item.label}
                </Typography>
              ) : (
                <Link
                  key={i}
                  component='button'
                  variant='body2'
                  color='text.secondary'
                  underline='hover'
                  onClick={() =>
                    item.index === -1
                      ? navigateToRoot()
                      : navigateToBreadcrumb(item.index)
                  }
                  sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {item.label}
                </Link>
              )
            })}
          </Breadcrumbs>
        </Box>

        {/* ---- Main area: directory list + optional doc panel ---- */}
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Directory list */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              minWidth: 0,
              maxWidth: '100vw'
            }}
          >
            {renderCurrentLevel()}
          </Box>

          {/* Slide-in document panel */}
          <Slide direction='left' in={!!openDoc} mountOnEnter unmountOnExit>
            <Box
              sx={{
                display: 'flex',
                position: { xs: 'fixed', md: 'relative' },
                top: { xs: 0, md: 'auto' },
                left: { xs: 0, md: 'auto' },
                right: { xs: 0, md: 'auto' },
                bottom: { xs: 0, md: 'auto' },
                zIndex: { xs: 1300, md: 'auto' },
                width: { xs: '100%', md: 'auto' },
                height: { xs: '100%', md: 'auto' }
              }}
            >
              {openDoc && (
                <DocumentPanel
                  document={openDoc}
                  context={openDocContext}
                  onClose={closeDoc}
                  userId={userId}
                />
              )}
            </Box>
          </Slide>
        </Box>
      </Paper>
    </>
  )
}

export default MisDocumentosDirectory
